import { ShieldAlert, CloudRain, Droplets, Truck, Wind, AlertTriangle, Pipette } from 'lucide-react';

export interface ToolboxTalkContent {
  id: string;
  title: string;
  topic: string;
  icon: any;
  tags: string[];
  description: string;
  hazard: string;
  bestPractices: string[];
  discussionPoints: string[];
  proTip: string;
  highlight?: boolean;
}

export const toolboxTalks: ToolboxTalkContent[] = [
  {
    id: 'stop-work-authority',
    title: "Stop Work Authority: Preventing Discharges",
    topic: "Compliance Leadership",
    icon: ShieldAlert,
    tags: ["Leadership", "Compliance"],
    highlight: true,
    description: "Empowering every crew member to prevent an illicit discharge before it happens.",
    hazard: "Uncontrolled runoff entering a storm drain can create serious environmental and enforcement risk. Stop the discharge and follow the project's response procedure.",
    bestPractices: [
      "Any worker can call a 'Time Out' if they see a BMP failure.",
      "No one will be reprimanded for stopping work to protect a drain.",
      "Check perimeters immediately if you see a change in water color (turbidity).",
      "Ensure all pumps are discharging to a sediment bag or basin, not the street."
    ],
    discussionPoints: [
      "Where are the two most sensitive discharge points on this specific site?",
      "Who has the spill kit keys right now?",
      "If a silt fence fails during a storm, do we have backup rolls on standby?"
    ],
    proTip: "The EPA doesn't care who made the mess; they care that it wasn't stopped. Stop work, fix the BMP, then resume."
  },
  {
    id: 'spill-response-basics',
    title: "Spill Response: Stop, Contain, Clean",
    topic: "Pollution Prevention",
    icon: AlertTriangle,
    tags: ["Spills", "Chemicals"],
    description: "The immediate steps required when fuel, hydraulic fluid, or oil hits the ground.",
    hazard: "A single gallon of spilled fuel can contaminate a million gallons of groundwater. Failure to report a spill can lead to criminal negligence charges.",
    bestPractices: [
      "STOP the source: Shut off the pump, close the valve, or upright the can immediately.",
      "CONTAIN the spread: Use absorbent snakes or build a dirt berm to keep it from reaching a drain.",
      "CLEAN it up: Excavate contaminated soil. NEVER hose a spill down the drain.",
      "REPORT: Notify the superintendent immediately, no matter how small the spill."
    ],
    discussionPoints: [
      "Where is the nearest spill kit located right now?",
      "What do we do with used absorbent pads? (Hint: They don't go in the regular dumpster)",
      "Who do you call if a hydraulic line bursts?"
    ],
    proTip: "The faster you act, the smaller the cleanup bill. A shovel of dirt in the first minute is worth a vac-truck an hour later."
  },
  {
    id: 'track-out-prevention',
    title: "Track-Out: Leaving it at the Gate",
    topic: "Perimeter Control",
    icon: Truck,
    tags: ["Exits", "Public Relations"],
    description: "Keeping mud off public roads to prevent safety hazards and community complaints.",
    hazard: "Mud on public roads is a safety hazard for vehicles and the #1 reason neighbors call the city inspector.",
    bestPractices: [
      "All vehicles MUST use the stabilized construction exit (gravel pad or rumble strip).",
      "Inspect tires before leaving. Remove large clods of dirt manually.",
      "If track-out occurs, SWEEP it immediately. Do not wash it into the storm drain.",
      "Refresh the gravel pad if it is filled with sediment."
    ],
    discussionPoints: [
      "Are delivery drivers using the correct exit?",
      "Is the gravel pad long enough (50ft) to be effective?",
      "Do we have a broom or sweeper attachment available?"
    ],
    proTip: "If you see mud on the road, don't wait for the street sweeper. Hand sweep the heavy stuff immediately to show 'good faith' effort."
  },
  {
    id: 'concrete-washout-discipline',
    title: "Concrete Washout: The $60k Mistake",
    topic: "Pollution Prevention",
    icon: Droplets,
    tags: ["Concrete", "P2"],
    description: "Why concrete washout is the most scrutinized BMP by EPA auditors.",
    hazard: "Concrete washout water has a pH level similar to liquid Drano. It kills aquatic life and ruins soil chemistry instantly.",
    bestPractices: [
      "Wash out ONLY in the designated, lined pit. Never in a hole in the dirt.",
      "Check the liner for tears before every delivery.",
      "If the pit is 75% full, call for a vacuum truck immediately.",
      "Pick up any 'hardened' concrete leaks in the drive aisles daily."
    ],
    discussionPoints: [
      "Is the washout signage clearly visible to new drivers?",
      "Have we seen any drivers trying to wash out in the curb and gutter?",
      "Where is the backup liner stored?"
    ],
    proTip: "A single 'leak' into a storm drain can trigger a full-site audit. Keep the washout tight."
  },
  {
    id: 'dust-control',
    title: "Dust Control: It's Not Just Dirt",
    topic: "Air & Water Quality",
    icon: Wind,
    tags: ["Air Quality", "Maintenance"],
    description: "Managing windborne sediment to protect neighbors and storm drains.",
    hazard: "Dust isn't just a nuisance; it settles in storm drains and becomes sediment pollution during the next rain.",
    bestPractices: [
      "Water active haul roads at least 3 times a day during dry weather.",
      "Limit vehicle speeds to 15 MPH on site.",
      "Cover stockpiles if they will sit inactive for more than 14 days.",
      "Stop grading activities during high wind warnings."
    ],
    discussionPoints: [
      "Is the water truck running frequently enough?",
      "Which areas of the site are generating the most dust today?",
      "Do we need to apply a tackifier or mulch to inactive areas?"
    ],
    proTip: "If you can see a dust cloud leaving the site boundary, you are already in violation. Water early and often."
  },
  {
    id: 'dewatering-safety',
    title: "Dewatering: Pumping without Polluting",
    topic: "Water Management",
    icon: Pipette,
    tags: ["Dewatering", "High Risk"],
    description: "Safe procedures for removing standing water from trenches and excavations.",
    hazard: "Pumping muddy trench water directly into a storm drain is an illegal discharge. It bypasses all perimeter controls.",
    bestPractices: [
      "NEVER pump directly to a street, inlet, or creek.",
      "Discharge into a sediment filter bag (dirt bag) located on a grassy area.",
      "Monitor the discharge constantly. If the bag rips or water runs clear then muddy, STOP immediately.",
      "Ensure the hose is secured and won't whip around."
    ],
    discussionPoints: [
      "Do we have spare dewatering bags on site?",
      "Where is the approved discharge location for this week's excavation?",
      "Who is assigned to watch the pump while it's running?"
    ],
    proTip: "Treat dewatering water like hazardous waste until it is filtered. It is the fastest way to turn a clean creek brown."
  },
  {
    id: 'rain-event-readiness',
    title: "The 24-Hour Storm Countdown",
    topic: "Weather Readiness",
    icon: CloudRain,
    tags: ["Weather", "Urgent"],
    description: "What needs to happen the moment a 50% rain chance hits the forecast.",
    hazard: "A surprise 1-inch rain event can move more sediment in 1 hour than a month of construction activity.",
    bestPractices: [
      "Secure all loose trash and debris that could float into inlets.",
      "Cover all small dirt stockpiles with plastic or tarps.",
      "Check that all 'J-Hooks' on silt fences are clear of debris.",
      "Ensure construction exits are refreshed with clean stone to prevent tracking."
    ],
    discussionPoints: [
      "If it rains tonight, who is the first person on-site tomorrow to inspect?",
      "Are there any slopes currently 'raw' that need straw or blankets today?",
      "Is the fuel tank secondary containment empty of rainwater?"
    ],
    proTip: "Most fines happen because a site wasn't 'buttoned up' before a weekend storm. Check the forecast every Friday."
  }
];
